import React, { useEffect, useState, useCallback, ChangeEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cartApi } from "../services/api/cartApi";
import { productApi } from "../services/api/productApi";
import { useAuth } from "../contexts/AuthContext";
import { ICart, ICartItem, IProduct } from "../types";
import { notifyCartUpdate } from "../utils/cartUpdateUtils";
import { toast } from "react-toastify";
import { paymentsApi } from "../services/api/paymentsApi";
import { ordersApi } from "../services/api/orderApi";
import ImageWithLoader from "../components/ImageWithLoader";
const canvasOptions = [
  { name: "Original Painting Canvas", price: 4000 },
  { name: "Printed Copy", price: 800 },
  { name: "Digital Copy", price: 500 },
];

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();
  const [cart, setCart] = useState<ICart | null>(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [copyLink, setCopyLink] = useState(false);
  const [frequentlyPurchased, setFrequentlyPurchased] = useState<IProduct[]>(
    [],
  );
  const [notes] = useState("");
  const [noRefundsPolicyAccepted, setNoRefundsPolicyAccepted] = useState(false);
  const [policyError, setPolicyError] = useState("");
  const [searchParams] = useSearchParams();
  const [selectedAddOns, setSelectedAddOns] = useState<Record<string, boolean>>(
    {},
  );
  const [selectedCanvasOption, setSelectedCanvasOption] = useState<any>(null);

  const handleCanvasOptionChange = async (option: any) => {
    console.log("optinos", option);
    setSelectedCanvasOption(option);
    const obj = {
      name: option.name,
      price: option.price,
    };
    await cartApi.updateCartAddOns([obj]);
    fetchCart();

    //  const newSelectedAddOns = { ...selectedAddOns };
    //           if (isChecked) {
    //               newSelectedAddOns[addOnName] = true;
    //           } else {
    //               delete newSelectedAddOns[addOnName];
    //           }
    //           setSelectedAddOns(newSelectedAddOns);

    //           // Convert to array format for API
    //           const addOnsArray = Object.keys(newSelectedAddOns).map(name => {
    //               const addOnPrices: Record<string, number> = {
    //                   'High resolution upgrade': 5,
    //                   'Unbranded or Watermark Free edition': 10,
    //                   'Print Ready edition': 15
    //               };
    //               return {
    //                   name,
    //                   price: addOnPrices[name] || 0
    //               };
    //           });

    //           await cartApi.updateCartAddOns(addOnsArray);
    //           fetchCart(); // Refresh cart to get updated total

    // if you want to update cart price or totals — do it here
    console.log("Selected:", option);
  };

  const fetchCartItem = async (productIds: string[]) => {
    try {
      const res = await cartApi.getCart({ ids: productIds.join(",") });
      setCart(res.data);
      // Initialize selected add-ons from cart
      if (res.data.addOns && Array.isArray(res.data.addOns)) {
        const addOnsMap: Record<string, boolean> = {};
        res.data.addOns.forEach((addOn: any) => {
          if (addOn.name) {
            addOnsMap[addOn.name] = true;
          }
        });
        console.log("res?.data?.addOns?.[0]", res?.data?.addOns?.[0]);
        setSelectedAddOns(addOnsMap);
      } else {
        setSelectedAddOns({});
      }
    } catch (error) {
      console.error("Failed to fetch shared products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = useCallback(async () => {
    if (!isLoggedIn) {
      toast.error("Please login to view your cart");
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const response = await cartApi.getCart();
      setCart(response.data);

      if (response.data.items.length === 0) {
        await cartApi.updateCartAddOns([]);
      }
      // Initialize selected add-ons from cart
      if (response.data.addOns) {
        const addOnsMap: Record<string, boolean> = {};
        response.data.addOns.forEach((addOn: any) => {
          addOnsMap[addOn.name] = true;
        });
        setSelectedCanvasOption(response?.data?.addOns?.[0]);
        setSelectedAddOns(addOnsMap);
      } else {
        setSelectedAddOns({});
        setSelectedCanvasOption({});
      }

      notifyCartUpdate();
    } catch (error: any) {
      console.error("Error fetching cart:", error);

      if (error?.response?.status === 401) {
        toast.error("Please login to view your cart");
        navigate("/login");
      } else {
        toast.error("Failed to load cart");
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, navigate]);

  const fetchFrequentlyPurchased = useCallback(async () => {
    try {
      const response = await productApi.getProducts({
        page: 1,
        limit: 2,
        sortBy: "popularity",
        sortOrder: "desc",
      });
      setFrequentlyPurchased(response.data.products);
    } catch (error) {
      console.error("Error fetching frequently purchased:", error);
    }
  }, []);

  useEffect(() => {
    const itemsParam = searchParams.get("items");
    if (itemsParam) {
      const productIds = itemsParam.split(",");
      fetchCartItem(productIds);
    } else if (isLoggedIn) {
      fetchCart();
    }

    fetchFrequentlyPurchased();
  }, [isLoggedIn, searchParams, fetchCart, fetchFrequentlyPurchased]);

  useEffect(() => {
    setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 0);
  }, []);

  const handleRemoveItem = async (productId: string) => {
    try {
      setRemoving(productId);

      await cartApi.removeCartItem(productId);
      toast.success("Item removed from cart");
      fetchCart(); // Refresh cart
    } catch (error: any) {
      console.error("Error removing item:", error);

      if (error?.response?.status === 401) {
        toast.error("Please login to manage your cart");
        navigate("/login");
      } else {
        toast.error("Failed to remove item");
      }
    } finally {
      setRemoving(null);
    }
  };

  const handleProceedToPayment = async () => {
    if (!noRefundsPolicyAccepted) {
      setPolicyError("Please accept the No Refunds Policy before proceeding.");
      return;
    }
    setPolicyError("");

    if (!isLoggedIn) {
      toast.error("Please login to proceed with checkout");
      navigate("/login");
      return;
    }

    if (!cart || cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    try {
      setProcessingCheckout(true);

      // Check if user owns any products in cart
      const productIds = cart.items.map((item) => item.product._id);
      const ownershipResponse: any =
        await ordersApi.checkMultipleProductsOwnership(productIds);

      if (ownershipResponse.success && ownershipResponse.data?.ownershipMap) {
        const ownedProducts = Object.entries(
          ownershipResponse.data.ownershipMap,
        )
          .filter(([_, owned]) => owned)
          .map(([productId]) => {
            const item = cart.items.find((i) => i.product._id === productId);
            return item?.product.name || "Unknown Product";
          });

        if (ownedProducts.length > 0) {
          toast.error(
            `You already own: ${ownedProducts.join(", ")}. Please remove them from cart.`,
          );
          setProcessingCheckout(false);
          return;
        }
      }

      toast.info("Redirecting to payment...");

      // Create checkout session
      const response = await paymentsApi.createCheckoutSession("", {
        addressRequired: true,
      });


      // Check different possible response structures
      const checkoutUrl = response?.url || response?.data?.url;

      if (checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl;
      } else {
        console.error("Response structure:", response);
        throw new Error("No checkout URL received");
      }
    } catch (error: any) {
      console.error("Error creating checkout session:", error);

      if (error?.response?.status === 401) {
        toast.error("Please login to proceed with checkout");
        navigate("/login");
      } else {
        toast.error(
          error?.response?.data?.message ||
            "Failed to create checkout session. Please try again.",
        );
      }
      setProcessingCheckout(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      setCopyLink(true);
      const productIds = cart?.items?.map((item) => item.product?._id) || [];

      const baseUrl = window.location.origin;
      const link = `${baseUrl}/cart?items=${productIds.join(",")}`;

      await navigator.clipboard.writeText(link);

      setTimeout(() => setCopyLink(false), 1500);
    } catch (error: any) {
      console.error("Error copy link:", error);
      setCopyLink(false);
    }
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setNoRefundsPolicyAccepted(checked);

    // ✅ If checked, instantly remove error
    if (checked) {
      setPolicyError("");
    }
  };

  const handleAddOnChange = async (
    addOnName: string,
    price: number,
    isChecked: boolean,
  ) => {
    try {
      const newSelectedAddOns = { ...selectedAddOns };
      if (isChecked) {
        newSelectedAddOns[addOnName] = true;
      } else {
        delete newSelectedAddOns[addOnName];
      }
      setSelectedAddOns(newSelectedAddOns);

      // Convert to array format for API
      const addOnsArray = Object.keys(newSelectedAddOns).map((name) => {
        const addOnPrices: Record<string, number> = {
          "High resolution upgrade": 5,
          "Unbranded or Watermark Free edition": 10,
          "Print Ready edition": 15,
        };
        return {
          name,
          price: addOnPrices[name] || 0,
        };
      });

      await cartApi.updateCartAddOns(addOnsArray);
      fetchCart(); // Refresh cart to get updated total
    } catch (error: any) {
      console.error("Error updating add-ons:", error);
      toast.error(error?.response?.data?.message || "Failed to update add-ons");
      // Revert selection on error
      fetchCart();
    }
  };

  const addOnOptions = [
    { name: "High resolution upgrade", price: 5 },
    { name: "Unbranded or Watermark Free edition", price: 10 },
    { name: "Print Ready edition", price: 15 },
  ];

  // const handleProceedToCheckout = () => {
  //   if (!isLoggedIn) {
  //     toast.error("Please login to proceed with checkout");
  //     navigate("/login");
  //     return;
  //   }

  //   if (!cart || cart.items.length === 0) {
  //     toast.error("Your cart is empty");
  //     return;
  //   }

  //   // Navigate to checkout page
  //   navigate("/checkout");
  // };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading cart...</p>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="container py-5">
        {/* Page Navigation */}
        <div className="collection_details_templet_banner d-sm-block d-none">
          <div className="page_navegation_area">
            <div className="container">
              <div className="page_navegation font_two">
                <p>
                  <a href="/">Home</a> /{" "}
                  <span className="active fw_700">Cart</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center py-5">
          <div className="empty_cart_area">
            <div className="mb-4" style={{ display: "inline-block" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="200"
                height="200"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ccc"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </div>
            <h3 className="mb-3">Your cart is empty</h3>
            <p className="mb-4">
              Looks like you haven't added any items to your cart yet.
            </p>

            <a href="/products?collection=Canvas" className="blue_btn">
              Continue Shopping
            </a>
          </div>
        </div>
      </div>
    );
  }

  const total = cart.totalAmount;

  return (
    <>
      {/* Page Navigation */}
      <div className="collection_details_templet_banner d-sm-block d-none">
        <div className="page_navegation_area">
          <div className="container">
            <div className="page_navegation font_two">
              <p>
                <a href="/">Home</a> /{" "}
                <span className="active fw_700">Cart</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Cart Section */}
      <section className="cart_section">
        <div className="cart_sec">
          <div className="container">
            <div className="cart_page_area">
              <div className="row">
                {/* Left Column - Cart Items */}
                <div className="col-xl-8">
                  <div className="cart_items_area">
                    {/* Cart Items List */}
                    <div className="cart_items_list">
                      {cart?.items?.map((item: ICartItem) => (
                        <div key={item?.product?._id} className="cart_item_box">
                          <div className="cart_item_inner">
                            {/* Product Image */}
                            <div className="cart_item_img">
                              <ImageWithLoader
                                src={
                                  item?.product?.image ||
                                  "/assets/images/dummy_product.png"
                                }
                                alt={item?.product?.name || "Product"}
                                fallbackSrc="/assets/images/dummy_product.png"
                              />
                            </div>

                            {/* Product Details */}
                            <div className="cart_item_details">
                              <h4 className="cart_item_title fw_500">
                                {item?.product?.name}
                              </h4>
                              <p className="cart_item_price fw_700">
                                ${item?.price?.toFixed(2)}
                              </p>
                              <p className="cart_item_dimensions">
                                <span className="fw_700">Dimensions:</span>{" "}
                                <span className="fw_400">
                                  {item?.product?.fileDetails
                                    ?.recommendedPrintSize ||
                                    item?.product?.fileDetails?.resolution ||
                                    "N/A"}
                                </span>
                              </p>
                              {(item?.selectedSize ||
                                item?.selectedFrame ||
                                item?.selectedCanvasWrap ||
                                item?.selectedPrintMaterial) && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    fontSize: 13,
                                    lineHeight: "18px",
                                  }}
                                >
                                  {item?.selectedSize?.name && (
                                    <p>
                                      <span className="fw_700">Size:</span>{" "}
                                      <span className="fw_400">
                                        {item.selectedSize.name}
                                      </span>
                                    </p>
                                  )}

                                  {item?.selectedFrame?.name && (
                                    <p>
                                      <span className="fw_700">Frame:</span>{" "}
                                      <span className="fw_400">
                                        {item.selectedFrame.name}
                                      </span>
                                    </p>
                                  )}

                                  {item?.selectedCanvasWrap?.name && (
                                    <p>
                                      <span className="fw_700">
                                        Canvas Wrap:
                                      </span>{" "}
                                      <span className="fw_400">
                                        {item.selectedCanvasWrap.name}
                                      </span>
                                    </p>
                                  )}

                                  {item?.selectedPrintMaterial?.name && (
                                    <p>
                                      <span className="fw_700">Material:</span>{" "}
                                      <span className="fw_400">
                                        {item.selectedPrintMaterial.name}
                                      </span>
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Digital Product Label and Remove */}
                            <div className="cart_item_actions">
                              {/* <div className="quantity_selector d-flex align-items-center">
                                  <span className="quantity_display">Digital</span>
                                </div> */}
                              <button
                                className="remove_item_btn"
                                onClick={() =>
                                  handleRemoveItem(item?.product?._id)
                                }
                                disabled={removing === item?.product?._id}
                                title="Remove item"
                              >
                                {/* <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <polyline points="3,6 5,6 21,6"></polyline>
                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                  </svg> */}
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <path
                                    d="M18.75 3.00001H15.191C14.291 3.00001 14.252 2.88601 14.005 2.14601L13.805 1.53901C13.6563 1.09026 13.3698 0.699889 12.9863 0.423542C12.6027 0.147195 12.1417 -0.00102599 11.669 5.34577e-06H7.831C7.35861 -0.000604649 6.89807 0.147812 6.51492 0.424129C6.13178 0.700446 5.84555 1.09058 5.697 1.53901L5.497 2.14601C5.25 2.88601 5.211 3.00001 4.311 3.00001H0.75C0.551088 3.00001 0.360322 3.07902 0.21967 3.21968C0.0790175 3.36033 0 3.55109 0 3.75001C0 3.94892 0.0790175 4.13968 0.21967 4.28034C0.360322 4.42099 0.551088 4.50001 0.75 4.50001H2.05L2.817 16C2.80732 16.4832 2.89944 16.963 3.08735 17.4082C3.27527 17.8534 3.55479 18.2541 3.90772 18.5842C4.26066 18.9143 4.67913 19.1665 5.13591 19.3243C5.59269 19.482 6.07756 19.5419 6.559 19.5H12.945C13.4264 19.5419 13.9113 19.482 14.3681 19.3243C14.8249 19.1665 15.2433 18.9143 15.5963 18.5842C15.9492 18.2541 16.2287 17.8534 16.4166 17.4082C16.6046 16.963 16.6967 16.4832 16.687 16L17.45 4.50001H18.75C18.9489 4.50001 19.1397 4.42099 19.2803 4.28034C19.421 4.13968 19.5 3.94892 19.5 3.75001C19.5 3.55109 19.421 3.36033 19.2803 3.21968C19.1397 3.07902 18.9489 3.00001 18.75 3.00001ZM7.12 2.01301C7.16946 1.86356 7.2648 1.73352 7.39246 1.64141C7.52012 1.54931 7.67358 1.49982 7.831 1.50001H11.669C11.8264 1.49982 11.9799 1.54931 12.1075 1.64141C12.2352 1.73352 12.3305 1.86356 12.38 2.01301L12.58 2.62001C12.623 2.75101 12.668 2.87801 12.716 3.00001H6.781C6.829 2.87701 6.874 2.75001 6.917 2.62001L7.12 2.01301ZM15.188 15.899C15.2099 16.1931 15.1656 16.4884 15.0584 16.7631C14.9511 17.0378 14.7836 17.285 14.5683 17.4865C14.3529 17.688 14.0951 17.8386 13.8138 17.9273C13.5326 18.016 13.235 18.0405 12.943 17.999H6.557C6.26501 18.0405 5.96744 18.016 5.68618 17.9273C5.40491 17.8386 5.14711 17.688 4.93173 17.4865C4.71635 17.285 4.54888 17.0378 4.44163 16.7631C4.33438 16.4884 4.29009 16.1931 4.312 15.899L3.55 4.50001H4.307C4.432 4.50001 4.535 4.48701 4.648 4.47901C4.68139 4.48869 4.7155 4.49572 4.75 4.50001H14.75C14.7838 4.49561 14.8173 4.48859 14.85 4.47901C14.963 4.48701 15.066 4.50001 15.191 4.50001H15.95L15.188 15.899ZM12.5 8.75001V13.75C12.5 13.9489 12.421 14.1397 12.2803 14.2803C12.1397 14.421 11.9489 14.5 11.75 14.5C11.5511 14.5 11.3603 14.421 11.2197 14.2803C11.079 14.1397 11 13.9489 11 13.75V8.75001C11 8.55109 11.079 8.36033 11.2197 8.21968C11.3603 8.07902 11.5511 8.00001 11.75 8.00001C11.9489 8.00001 12.1397 8.07902 12.2803 8.21968C12.421 8.36033 12.5 8.55109 12.5 8.75001ZM8.5 8.75001V13.75C8.5 13.9489 8.42098 14.1397 8.28033 14.2803C8.13968 14.421 7.94891 14.5 7.75 14.5C7.55109 14.5 7.36032 14.421 7.21967 14.2803C7.07902 14.1397 7 13.9489 7 13.75V8.75001C7 8.55109 7.07902 8.36033 7.21967 8.21968C7.36032 8.07902 7.55109 8.00001 7.75 8.00001C7.94891 8.00001 8.13968 8.07902 8.28033 8.21968C8.42098 8.36033 8.5 8.55109 8.5 8.75001Z"
                                    fill="#112C3B"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column - Order Summary */}
                <div className="col-xl-4">
                  <div className="order_summary_area">
                    <div className="order_summary_box">
                      {/* Order Summary */}
                      <div className="order_summary_details">
                        <div className="summary_row d-flex justify-content-between">
                          <span style={{ fontFamily: "var(--font_one)" }}>
                            Total Items:
                          </span>
                          <span>{cart.totalItems}</span>
                        </div>
                        <div className="summary_row d-flex justify-content-between">
                          <span style={{ fontFamily: "var(--font_one)" }}>
                            Subtotal ({cart?.totalItems} Items):
                          </span>
                          <span>
                            $
                            {(
                              cart?.totalAmount -
                              (cart?.addOns?.reduce(
                                (sum: number, addOn: any) =>
                                  sum + (addOn.price || 0),
                                0,
                              ) || 0)
                            ).toFixed(2)}
                          </span>
                        </div>
                        {cart?.addOns && cart.addOns.length > 0 && (
                          <>
                            {cart.addOns.map((addOn: any, index: number) => (
                              <div
                                key={index}
                                className="summary_row d-flex justify-content-between"
                              >
                                <span
                                  style={{
                                    fontFamily: "var(--font_one)",
                                    fontSize: "14px",
                                  }}
                                >
                                  {addOn.name}:
                                </span>
                                <span style={{ fontSize: "14px" }}>
                                  ${addOn.price.toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </>
                        )}
                        <hr className="summary_divider" />
                        <div className="summary_row total_row d-flex justify-content-between">
                          <span
                            className="fw_500"
                            style={{ fontFamily: "var(--font_one)" }}
                          >
                            Total:
                          </span>
                          <span className="fw_500">${total.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Proceed Button */}
                      <div className="proceed_btn_area">
                        <button
                          onClick={handleProceedToPayment}
                          disabled={processingCheckout}
                          className="btn btn-dark w-100 py-3 rounded-pill"
                          style={{
                            cursor: processingCheckout
                              ? "not-allowed"
                              : "pointer",
                            opacity: processingCheckout ? 0.7 : 1,
                          }}
                        >
                          {processingCheckout ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Processing...
                            </>
                          ) : (
                            "Proceed"
                          )}
                        </button>
                      </div>
                      {/*Copy link Button */}
                      {user?.isCartShared && (
                        <div>
                          <button
                            className="btn btn-dark w-100 py-3 mt-2 rounded-pill"
                            onClick={handleCopyLink}
                            disabled={copyLink}
                          >
                            {copyLink ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Generate Link...
                              </>
                            ) : (
                              "Copy Link"
                            )}
                          </button>
                        </div>
                      )}

                      <div className="Checkout_model_info_area_checking form-check my-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="noRefundsPolicy"
                          name="noRefundsPolicy"
                          checked={noRefundsPolicyAccepted}
                          onChange={(e) => handleCheckboxChange(e)}
                          style={{ accentColor: "var(--color_darkblue)" }}
                        />
                        <label
                          className="font_two form-check-label small"
                          htmlFor="noRefundsPolicy"
                        >
                          I have read and agree to the{" "}
                          <a
                            href="/terms-and-conditions"
                            target="_blank"
                            className="text-link"
                          >
                            No Returns / No Refunds{" "}
                          </a>
                          Terms and Conditions, the DigiCartt Privacy Policy,
                          and the Terms of Service. I understand that all sales
                          are final. <span className="text-danger">*</span>
                        </label>
                        {policyError && !noRefundsPolicyAccepted && (
                          <div className="text-danger small mt-1">
                            {policyError}
                          </div>
                        )}
                      </div>

                      {/* Add-ons Section - Only show if cart has digivision products */}
                      {cart?.items?.some((item: ICartItem) => {
                        const product = item.product as any;
                        const category = product?.categoryId as any;
                        return category?.type === "digivision";
                      }) && (
                        <div className="addons_section Checkout_model_info_area_checking mt-3">
                          <h5
                            className="mb-3"
                            style={{
                              fontFamily: "var(--font_one)",
                              fontSize: "16px",
                            }}
                          >
                            Add-ons
                          </h5>
                          {addOnOptions.map((addOn) => (
                            <div key={addOn.name} className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`addon-${addOn.name}`}
                                checked={selectedAddOns[addOn.name] || false}
                                onChange={(e) =>
                                  handleAddOnChange(
                                    addOn.name,
                                    addOn.price,
                                    e.target.checked,
                                  )
                                }
                              />
                              <label
                                className="font_two form-check-label small"
                                htmlFor={`addon-${addOn.name}`}
                                style={{ cursor: "pointer" }}
                              >
                                {addOn.name} (+${addOn.price.toFixed(2)})
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                      {cart?.items?.some((item: ICartItem) => {
                        const product = item.product as any;
                        const category = product?.categoryId as any;
                        return category?.type === "canvas";
                      }) && (
                        <div className="addons_section Checkout_model_info_area_checking mt-3">
                          <h5
                            className="mb-3"
                            style={{
                              fontFamily: "var(--font_one)",
                              fontSize: "16px",
                            }}
                          >
                            Add-ons
                          </h5>

                          {canvasOptions.map((option) => (
                            <div key={option.name} className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="canvasOption"
                                id={`canvas-${option.name}`}
                                checked={
                                  selectedCanvasOption?.name === option.name
                                }
                                onChange={() =>
                                  handleCanvasOptionChange(option)
                                }
                              />

                              <label
                                className="font_two form-check-label small"
                                htmlFor={`canvas-${option.name}`}
                                style={{ cursor: "pointer" }}
                              >
                                {option.name} (+${option.price.toFixed(2)})
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Frequently Purchased Section */}
                    <div className="frequently_purchased_area">
                      <div className="frequently_purchased_header d-flex justify-content-between align-items-center">
                        <h3 className="frequently_purchased_title fw_500">
                          Frequently Purchased
                        </h3>
                        <a
                          href="/products?collection=Canvas"
                          className="view_all_link"
                        >
                          All
                        </a>
                      </div>

                      <div className="frequently_purchased_items">
                        {frequentlyPurchased.map((product) => (
                          <div key={product._id} className="frequently_item">
                            <div className="frequently_item_img position-relative">
                              <img
                                src={
                                  product.image ||
                                  "/assets/images/dummy_product.png"
                                }
                                alt={product.name}
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  navigate(`/product/${product._id}`)
                                }
                                onError={(e) => {
                                  e.currentTarget.src =
                                    "/assets/images/dummy_product.png";
                                }}
                              />
                              <button
                                className="wishlist_btn"
                                title="Add to Wishlist"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M18.8578 3.74469C14.8661 1.60383 12 4.7597 12 4.7597C12 4.7597 9.1338 1.60382 5.14211 3.74468C0.307394 6.33768 -0.0397768 16.492 12 21C24.0399 16.492 23.6926 6.33769 18.8578 3.74469Z"
                                    stroke="#112C3B"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                </svg>
                              </button>
                            </div>
                            <div className="frequently_item_details">
                              <h5
                                className="frequently_item_title"
                                style={{ fontFamily: "var(--font_one)" }}
                              >
                                {product.name}
                              </h5>
                              <p className="font_two frequently_item_price fw_700">
                                ${product.price.toFixed(2)}
                              </p>
                              <p className="frequently_item_dimensions">
                                <span className="fw_700">Dimensions:</span>{" "}
                                <span>
                                  {product?.fileDetails?.recommendedPrintSize ||
                                    product?.fileDetails?.resolution ||
                                    "N/A"}
                                </span>
                              </p>
                            </div>
                          </div>
                        ))}
                        {frequentlyPurchased.length === 0 && (
                          <div className="text-center py-4">
                            <p className="text-muted">
                              No frequently purchased items available
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Cart;
